/**
 * Host Global - Property Owner Reservation Details (Read-only)
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PropertyOwnerFooter from "examples/PropertyOwnerFooter";

import { propertyOwnerAPI } from "utils/api";

function ReservationDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [reservation, setReservation] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        setLoading(true);
        setMessage({ type: "", text: "" });
        const res = await propertyOwnerAPI.getReservationById(id);
        console.log("=== Frontend: Reservation Data ===");
        console.log("Full response:", res);
        console.log("res.data:", res.data);
        console.log("res.reservation:", res.reservation);
        console.log("res.data?.reservation:", res.data?.reservation);
        console.log("res.data?.guests:", res.data?.guests);
        console.log("res.reservation?.guests:", res.reservation?.guests);
        console.log("res.data?.reservation?.guests:", res.data?.reservation?.guests);
        console.log("=================================");

        // Handle both response formats: res.data.reservation or res.reservation or res.data
        const reservationData = res.data?.reservation || res.reservation || res.data;
        console.log("Using reservationData:", reservationData);
        console.log("reservationData.guests:", reservationData?.guests);
        setReservation(reservationData);
      } catch (error) {
        console.error("Error fetching reservation details:", error);
        setMessage({ type: "error", text: error.message || "Failed to load reservation details" });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchReservation();
  }, [id]);

  const currency = reservation?.finance?.currency || "EGP";
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    }).format(amount || 0);

  const statusChip = useMemo(() => {
    const s = reservation?.status || "booked";
    const label = s.replace("_", " ").toUpperCase();
    const color =
      s === "booked"
        ? "#1976d2"
        : s === "checked_in"
        ? "#4caf50"
        : s === "checked_out"
        ? "#9e9e9e"
        : "#f44336";
    return { label, color };
  }, [reservation]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <PropertyOwnerFooter />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/property-owner/reservations")}
            startIcon={<Icon>arrow_back</Icon>}
          >
            Back to Calendar
          </MDButton>
          {reservation?.reservation_number && (
            <Chip label={reservation.reservation_number} size="small" sx={{ fontWeight: 700 }} />
          )}
        </MDBox>

        {message.text && (
          <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {!reservation ? (
          <Card sx={{ p: 4, borderRadius: 3 }}>
            <MDTypography variant="h6" fontWeight="bold">
              Reservation not found
            </MDTypography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, borderRadius: 3 }}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                      {reservation.property?.property_name}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {reservation.property?.property_short_id
                        ? `ID: ${reservation.property.property_short_id}`
                        : ""}
                    </MDTypography>
                  </MDBox>
                  <Chip
                    label={statusChip.label}
                    sx={{ bgcolor: statusChip.color, color: "white", fontWeight: 700 }}
                    size="small"
                  />
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="info">event</Icon> Booking Details
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-in
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {new Date(reservation.booking?.check_in_date).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Check-out
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {new Date(reservation.booking?.check_out_date).toLocaleDateString()}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="body2" color="text.secondary">
                    Guests
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {reservation.booking?.number_of_guests || 0}
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Icon color="success">paid</Icon> Finance Details
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Total Amount
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" color="success.main">
                    {formatCurrency(reservation.finance?.total_amount)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Platform Fee
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium" color="warning.main">
                    {formatCurrency(reservation.finance?.platform_fee)}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text.secondary">
                    Deductions
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium" color="info.main">
                    {formatCurrency(reservation.finance?.deductions)}
                  </MDTypography>
                </MDBox>
                <Divider sx={{ my: 2 }} />
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
                    Net Revenue
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="bold" color="success.main">
                    {formatCurrency(reservation.finance?.owner_revenue_net)}
                  </MDTypography>
                </MDBox>
                {reservation.finance?.deductions_description ? (
                  <MDTypography variant="caption" color="text.secondary" mt={1} display="block">
                    Deduction note: {reservation.finance.deductions_description}
                  </MDTypography>
                ) : null}
              </Card>
            </Grid>

            {/* Guest Information - Replaces Client section */}
            {reservation.guests && reservation.guests.length > 0 ? (
              <Grid item xs={12}>
                <Card sx={{ p: 3, borderRadius: 3 }}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon color="info">people</Icon> Guests
                  </MDTypography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={3}>
                    {reservation.guests.map((guest, index) => {
                      // Check if reservation is checked out (handle multiple status formats)
                      const status = reservation.status?.toLowerCase() || "";
                      const isCheckedOut =
                        status === "checked_out" ||
                        status === "checkedout" ||
                        reservation.status === "CheckedOut";

                      return (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                          <Card
                            sx={{
                              p: 2.5,
                              borderRadius: 2,
                              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                              border: "1px solid",
                              borderColor: "grey.200",
                              "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
                            }}
                          >
                            <MDTypography
                              variant="subtitle1"
                              fontWeight="bold"
                              mb={1.5}
                              color="info.main"
                            >
                              Guest {index + 1}: {guest.name || "N/A"}
                            </MDTypography>

                            {/* Show ID photos side by side, only if NOT checked out */}
                            {!isCheckedOut && (guest.idFrontPhoto || guest.idBackPhoto) && (
                              <MDBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  gap: 2,
                                  alignItems: "flex-start",
                                  mt: 1.5,
                                  flexWrap: "nowrap",
                                }}
                              >
                                {guest.idFrontPhoto && (
                                  <MDBox sx={{ flexShrink: 0 }}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      display="block"
                                      mb={0.5}
                                    >
                                      ID Front
                                    </MDTypography>
                                    <MDBox
                                      component="img"
                                      src={guest.idFrontPhoto}
                                      alt={`Guest ${index + 1} ID Front`}
                                      sx={{
                                        width: 140,
                                        height: 95,
                                        objectFit: "cover",
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: "grey.300",
                                        cursor: "pointer",
                                        transition: "opacity 0.2s",
                                        "&:hover": { opacity: 0.85 },
                                      }}
                                      onClick={() => window.open(guest.idFrontPhoto, "_blank")}
                                    />
                                  </MDBox>
                                )}
                                {guest.idBackPhoto && (
                                  <MDBox sx={{ flexShrink: 0 }}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      fontWeight="medium"
                                      display="block"
                                      mb={0.5}
                                    >
                                      ID Back
                                    </MDTypography>
                                    <MDBox
                                      component="img"
                                      src={guest.idBackPhoto}
                                      alt={`Guest ${index + 1} ID Back`}
                                      sx={{
                                        width: 140,
                                        height: 95,
                                        objectFit: "cover",
                                        borderRadius: 1.5,
                                        border: "1px solid",
                                        borderColor: "grey.300",
                                        cursor: "pointer",
                                        transition: "opacity 0.2s",
                                        "&:hover": { opacity: 0.85 },
                                      }}
                                      onClick={() => window.open(guest.idBackPhoto, "_blank")}
                                    />
                                  </MDBox>
                                )}
                              </MDBox>
                            )}
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Card sx={{ p: 3, borderRadius: 3 }}>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Icon color="info">people</Icon> Guests
                  </MDTypography>
                  <Divider sx={{ mb: 2 }} />
                  <MDTypography variant="body2" color="text.secondary">
                    No guest information available
                  </MDTypography>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </MDBox>
      <PropertyOwnerFooter />
    </DashboardLayout>
  );
}

export default ReservationDetails;
